/* eslint-disable no-console */
/**
 * Hook simplifié pour la gestion des campagnes
 * Version fonctionnelle pour résoudre le problème du bouton "Lancer une campagne"
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CampaignFormData } from '@/types/campaignForm';
import { CampaignStatus } from '@/types/states';
import { logger } from '@/utils/productionLogger';

interface UseCampaignManagerSimpleOptions {
  userId?: string;
  draftId?: string | null;
  isDialog?: boolean;
  onSuccess?: () => void;
}

type SubmissionStep = 'idle' | 'validating' | 'updating' | 'finalizing' | 'sending_email' | 'complete';

export const useCampaignManagerSimple = (options: UseCampaignManagerSimpleOptions) => {
  const { userId, draftId, isDialog: _isDialog = false, onSuccess } = options;
  const { toast } = useToast();
  
  const [campaignId, setCampaignId] = useState<string | null>(draftId || null);
  const [isDraft, setIsDraft] = useState(!!draftId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStep, setSubmissionStep] = useState<SubmissionStep>('idle');

  const buildReferentsData = useCallback((data: CampaignFormData) => {
    return data.selected_referents || [];
  }, []);

  const createDraftOnDemand = useCallback(async (): Promise<string | null> => {
    if (!userId || campaignId) {
      return null;
    }

    try {
      const uniqueLink = crypto.randomUUID();

      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .insert({
          recruiter_id: userId,
          candidate_name: '',
          candidate_email: '',
          target_position: '',
          status: CampaignStatus.BROUILLON,
          is_draft: true,
          unique_link: uniqueLink,
        } as any)
        .select()
        .single();

      if (campaignError || !campaign) {
        logger.error('Erreur création brouillon', campaignError as Error);
        return null;
      }

      setCampaignId((campaign as any).id);
      setIsDraft(true);
      
      return (campaign as any).id;

    } catch (_error) {
      logger.error('Erreur lors de la création du brouillon', _error as Error);
      return null;
    }
  }, [userId, campaignId]);

  const saveDraft = useCallback(async (
    data: CampaignFormData, 
    currentStep = 1, 
    _isAnalysisRunning = false, 
    isAutoSave = false
  ) => {
    if (!userId) {
      toast({
        title: "Erreur",
        description: "Utilisateur non authentifié",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const referentsData = buildReferentsData(data);
      const referentsRequestedCount = referentsData.length || 0;
      
      let campaign, campaignError;
      
      if (isDraft && campaignId) {
        // Mise à jour d'un brouillon existant
        const result = await supabase
          .from('campaigns')
          .update({
            candidate_name: data.candidate_name,
            candidate_email: data.candidate_email,
            candidate_phone: data.candidate_phone,
            target_position: data.target_position,
            candidate_cv_url: data.candidate_cv_url,
            job_description_file_url: data.job_description_file_url,
            job_description_text: data.job_description_text,
            referents_requested_count: referentsRequestedCount,
            preferred_relation_types: referentsData.map(r => r.suggestedRole),
            selected_referents: referentsData, // ✅ FIX: Sauvegarder les référents sélectionnés
            selected_evaluation_criteria: data.selected_evaluation_criteria,
            current_step: currentStep,
            updated_at: new Date().toISOString()
          })
          .eq('id', campaignId)
          .eq('is_draft', true)
          .select()
          .single();
        
        campaign = result.data;
        campaignError = result.error;
      } else {
        // Création d'un nouveau brouillon
        const uniqueLink = crypto.randomUUID();
        
        const result = await supabase
          .from('campaigns')
          .insert({
            recruiter_id: userId,
            candidate_name: data.candidate_name,
            candidate_email: data.candidate_email,
            candidate_phone: data.candidate_phone,
            target_position: data.target_position,
            candidate_cv_url: data.candidate_cv_url,
            job_description_file_url: data.job_description_file_url,
            job_description_text: data.job_description_text,
            status: CampaignStatus.BROUILLON,
            is_draft: true,
            unique_link: uniqueLink,
            referents_requested_count: referentsRequestedCount,
            preferred_relation_types: referentsData.map(r => r.suggestedRole),
            selected_referents: referentsData, // ✅ FIX: Sauvegarder les référents sélectionnés
            selected_evaluation_criteria: data.selected_evaluation_criteria,
            current_step: currentStep,
          } as any)
          .select()
          .single();
        
        campaign = result.data;
        campaignError = result.error;
        
        if (!campaignError && campaign) {
          setCampaignId(campaign.id);
          setIsDraft(true);
        }
      }

      if (campaignError) {
        logger.error('Erreur sauvegarde brouillon', campaignError);
        throw campaignError;
      }

      if (!isAutoSave) {
        toast({
          title: "Brouillon sauvegardé",
          description: "Votre campagne a été sauvegardée en brouillon.",
        });
      }

      if (!isAutoSave && onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      logger.error('Erreur sauvegarde brouillon', error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la sauvegarde du brouillon",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [userId, campaignId, isDraft, buildReferentsData, toast, onSuccess]);

  const submitCampaign = useCallback(async (data: CampaignFormData) => {
    console.warn('🚀 DEBUG - submitCampaign appelé avec:', {
      userId,
      campaignId,
      isDraft,
      candidateName: data.candidate_name,
      candidateEmail: data.candidate_email
    });

    if (!userId) {
      console.error('❌ DEBUG - userId manquant');
      toast({
        title: "Erreur",
        description: "Utilisateur non authentifié",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmissionStep('validating');

    try {
      const referentsData = buildReferentsData(data);
      const referentsRequestedCount = referentsData.length || 0;
      
      console.log('📋 DEBUG - Données préparées:', {
        referentsCount: referentsRequestedCount,
        criteriaCount: data.selected_evaluation_criteria?.length || 0,
        hasCV: !!data.candidate_cv_url,
        hasJobDescription: !!(data.job_description_text || data.job_description_file_url)
      });
      
      let campaign, campaignError;
      
      if (isDraft && campaignId) {
        console.log('🔄 DEBUG - Mise à jour brouillon existant:', campaignId);
        setSubmissionStep('updating');
        
        // Mettre à jour le brouillon avec les dernières données avant finalisation
        const { error: updateError } = await supabase
          .from('campaigns')
          .update({
            candidate_name: data.candidate_name,
            candidate_email: data.candidate_email,
            target_position: data.target_position,
            candidate_cv_url: data.candidate_cv_url,
            job_description_file_url: data.job_description_file_url,
            job_description_text: data.job_description_text,
            referents_requested_count: referentsRequestedCount,
            preferred_relation_types: referentsData.map(r => r.suggestedRole),
            selected_referents: referentsData, // ✅ FIX: Sauvegarder les référents sélectionnés
            selected_evaluation_criteria: data.selected_evaluation_criteria,
            updated_at: new Date().toISOString()
          })
          .eq('id', campaignId)
          .eq('is_draft', true);
          
        if (updateError) {
          console.error('❌ DEBUG - Erreur mise à jour brouillon:', updateError);
          logger.error('Erreur mise à jour avant finalisation', updateError);
          throw updateError;
        }

        console.log('✅ DEBUG - Brouillon mis à jour, finalisation...');
        setSubmissionStep('finalizing');

        // Finaliser la campagne
        const { data: finalCampaign, error: finalizeError } = await supabase
          .from('campaigns')
          .update({
            status: CampaignStatus.EN_ATTENTE_CANDIDAT,
            is_draft: false,
            unique_link: `campaign-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            updated_at: new Date().toISOString()
          })
          .eq('id', campaignId)
          .select()
          .single();

        console.log('🔍 DEBUG - Résultat finalisation:', {
          finalCampaign,
          finalizeError,
          statusSet: CampaignStatus.EN_ATTENTE_CANDIDAT,
          statusReceived: finalCampaign?.status,
          isDraftSet: false,
          isDraftReceived: finalCampaign?.is_draft,
          campaignId: finalCampaign?.id
        });

        campaign = finalCampaign;
        campaignError = finalizeError;
        
        if (!campaignError && finalCampaign) {
          setIsDraft(false);
          
          // Vérification critique du statut avec correction automatique
          if (finalCampaign.status !== CampaignStatus.EN_ATTENTE_CANDIDAT) {
            console.warn('⚠️ DEBUG - STATUT INATTENDU! Correction automatique...', {
              expected: CampaignStatus.EN_ATTENTE_CANDIDAT,
              actual: finalCampaign.status,
              campaignId: finalCampaign.id
            });
            
            // Force update du statut si nécessaire
            try {
              const { error: forceUpdateError } = await supabase
                .from('campaigns')
                .update({ status: CampaignStatus.EN_ATTENTE_CANDIDAT })
                .eq('id', finalCampaign.id);
                
              if (forceUpdateError) {
                logger.error('Erreur correction statut', forceUpdateError);
              } else {
                console.log('✅ DEBUG - Statut corrigé avec succès');
                // Mettre à jour l'objet campaign pour la suite
                finalCampaign.status = CampaignStatus.EN_ATTENTE_CANDIDAT;
              }
            } catch (forceError) {
              logger.error('Exception correction statut', forceError as Error);
            }
          } else {
            console.log('✅ DEBUG - Statut correct après finalisation');
          }
        }
      } else {
        console.log('🆕 DEBUG - Création nouvelle campagne directe');
        setSubmissionStep('finalizing');
        
        // Créer une nouvelle campagne directement
        const uniqueLink = `campaign-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        const result = await supabase
          .from('campaigns')
          .insert({
            recruiter_id: userId,
            candidate_name: data.candidate_name,
            candidate_email: data.candidate_email,
            target_position: data.target_position,
            candidate_cv_url: data.candidate_cv_url,
            job_description_file_url: data.job_description_file_url,
            job_description_text: data.job_description_text,
            status: CampaignStatus.EN_ATTENTE_CANDIDAT,
            is_draft: false,
            unique_link: uniqueLink,
            referents_requested_count: referentsRequestedCount,
            preferred_relation_types: referentsData.map(r => r.suggestedRole),
            selected_referents: referentsData, // ✅ FIX: Sauvegarder les référents sélectionnés
            selected_evaluation_criteria: data.selected_evaluation_criteria,
          } as any)
          .select()
          .single();
        
        console.log('🔍 DEBUG - Résultat création directe:', {
          campaign: result.data,
          error: result.error,
          statusSet: CampaignStatus.EN_ATTENTE_CANDIDAT,
          statusReceived: result.data?.status
        });
        
        campaign = result.data;
        campaignError = result.error;
        
        // Vérification du statut pour création directe aussi
        if (!campaignError && result.data && result.data.status !== CampaignStatus.EN_ATTENTE_CANDIDAT) {
          console.warn('⚠️ DEBUG - STATUT INATTENDU lors création directe!', {
            expected: CampaignStatus.EN_ATTENTE_CANDIDAT,
            actual: result.data.status,
            campaignId: result.data.id
          });
        }
      }

      if (campaignError) {
        console.error('❌ DEBUG - Erreur création/finalisation campagne:', campaignError);
        logger.error('Erreur création campagne', campaignError);
        throw campaignError;
      }

      console.log('✅ DEBUG - Campagne créée/finalisée avec succès:', {
        campaignId: campaign?.id,
        status: campaign?.status,
        isDraft: campaign?.is_draft,
        candidateEmail: campaign?.candidate_email
      });

      logger.info('Campagne créée avec succès', { campaignId: campaign?.id });

      // Envoyer l'email de demande de références au candidat
      console.log('📧 DEBUG - Envoi email au candidat:', {
        campaignId: campaign?.id,
        candidateEmail: campaign?.candidate_email
      });
      setSubmissionStep('sending_email');

      try {
        const { error: candidateEmailError } = await supabase.functions.invoke('send-candidate-reminder', {
          body: {
            campaign_id: campaign?.id,
            type: 'referent_request',
          },
        });

        console.log('📧 DEBUG - Résultat envoi email:', {
          error: candidateEmailError,
          success: !candidateEmailError
        });

        if (candidateEmailError) {
          console.warn('⚠️ DEBUG - Échec envoi email:', candidateEmailError);
          logger.warn('Email candidat failed', candidateEmailError);
          toast({
            title: "Campagne créée avec avertissement",
            description: "La campagne a été créée mais l'email au candidat n'a pas pu être envoyé. Vous pouvez le renvoyer depuis le tableau de bord.",
            variant: "default",
          });
        } else {
          console.log('✅ DEBUG - Email envoyé avec succès');
          toast({
            title: "🎉 Campagne lancée avec succès !",
            description: `La campagne pour ${data.candidate_name} est maintenant active. L'email de demande de références a été envoyé.`,
          });
        }
      } catch (emailError) {
        console.error('❌ DEBUG - Exception envoi email:', emailError);
        logger.warn('Erreur envoi email candidat', emailError as Error);
        toast({
          title: "Campagne créée avec avertissement",
          description: "La campagne a été créée mais l'email au candidat n'a pas pu être envoyé.",
          variant: "default",
        });
      }

      // Déclencher le callback de succès
      console.log('🎉 DEBUG - Déclenchement callback de succès');
      setSubmissionStep('complete');
      
      if (onSuccess) {
        // Délai plus court pour une meilleure UX
        setTimeout(() => onSuccess(), 500);
      }

    } catch (error: any) {
      console.error('💥 DEBUG - Exception dans submitCampaign:', error);
      logger.error('Erreur création campagne', error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la création de la campagne",
        variant: "destructive",
      });
    } finally {
      
      setIsSubmitting(false);
      setSubmissionStep('idle');
    }
  }, [userId, campaignId, isDraft, buildReferentsData, toast, onSuccess]);

  return {
    campaignId,
    isDraft,
    isSubmitting,
    submissionStep,
    saveDraft,
    submitCampaign,
    createDraftOnDemand,
    setCampaignId,
    setIsDraft,
  };
};